/**
 * As notificações da pessoa (#536): o lead escreveu, e é com ela.
 *
 * ── O que chega pelo fluxo, e o que chega por aqui ────────────────────────
 *
 * O fluxo SSE diz só `notificacao:eu` — que há novidade (ADR-0004). O card
 * sai DESTA rota: quem escreveu, o trecho, a caixa. A autorização mora aqui,
 * e o escopo de caixa é reavaliado na leitura: quem deixou de atender uma
 * caixa entre a mensagem e a leitura não vê a notificação dela.
 *
 * ── Só as pendentes, e só as recentes ─────────────────────────────────────
 *
 * A rota devolve o que ainda não foi visto, da última hora, das mais novas
 * para trás. Card é interrupção: quem abre o CRM depois do almoço não quer
 * vinte cards do que aconteceu de manhã — isso é a fila da inbox e o sino.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';
import { InstanteSchema } from './valores.js';

export const TIPOS_DE_NOTIFICACAO = ['mensagem_recebida', 'mencao'] as const;

export const NotificacaoSchema = z
  .object({
    id: IdSchema,
    tipo: z.enum(TIPOS_DE_NOTIFICACAO),
    /**
     * Quem mencionou, na notificação de menção (#569): "Ana mencionou você".
     * Nulo na mensagem recebida, que já diz de quem é pelo `cliente`.
     */
    autor: z.string().nullable(),
    criada_em: InstanteSchema,
    /** A conversa a abrir no clique. */
    conversa_id: IdSchema,
    cliente: z
      .object({
        /** Nulo quando a identidade ainda não foi resolvida. A tela diz "Cliente". */
        nome_exibicao: z.string().nullable(),
        /**
         * Endereço da foto, servido por nós e assinado — nunca a URL do motor.
         * Vale cinco minutos. Nulo quando a conversa não é do motor de
         * conversas; quando o contato não tem foto, o endereço responde 404.
         * Nos dois casos a tela mostra as iniciais.
         */
        avatar_url: z.string().nullable(),
      })
      .strict(),
    mensagem: z
      .object({
        /**
         * O começo do que o lead escreveu, até 120 caracteres, com reticências
         * quando corta. Vazio quando a mensagem é só anexo: a tela decide o
         * que dizer — texto de interface não mora no contrato.
         */
        trecho: z.string(),
        /** Tipo do conteúdo quando não é texto (imagem, áudio, arquivo), como na inbox. */
        tipo: z.string().nullable(),
        enviada_em: InstanteSchema,
      })
      .strict(),
    caixa: z
      .object({
        referencia_externa: z.string(),
        nome: z.string().nullable(),
        /** Canal fino da matriz da A03 — nunca o `Channel::*` cru. */
        canal: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type Notificacao = z.infer<typeof NotificacaoSchema>;

/**
 * O aviso que uma automação mandou ao gestor (#903): a ação `notify_manager`.
 *
 * Não é card de mensagem: não tem quem escreveu nem trecho. Diz qual aviso
 * (`aviso`, uma chave do motor como `no_first_reply` — o texto é da tela) e,
 * quando há, a conversa a abrir. Fica até a pessoa dispensar: é um pedido de
 * atenção, não uma interrupção que passa.
 */
export const AvisoDeAutomacaoSchema = z
  .object({
    id: IdSchema,
    aviso: z.string(),
    criada_em: InstanteSchema,
    /** A conversa de que o aviso trata; nulo quando a regra não parte de uma. */
    conversa_id: IdSchema.nullable(),
  })
  .strict();

export type AvisoDeAutomacao = z.infer<typeof AvisoDeAutomacaoSchema>;

/** Teto de avisos de automação por leitura. */
export const MAXIMO_DE_AVISOS_DE_AUTOMACAO = 10;

export const NotificacoesSchema = z
  .object({
    itens: z.array(NotificacaoSchema),
    /** Os avisos de automação pendentes de quem pergunta (#903). */
    avisos: z.array(AvisoDeAutomacaoSchema),
  })
  .strict();

export type Notificacoes = z.infer<typeof NotificacoesSchema>;

/** Teto de itens por leitura — maior que isto já não é card, é fila. */
export const MAXIMO_DE_NOTIFICACOES = 20;

/** "Menções para mim" (#569): as dos últimos 30 dias, até este tanto. */
export const MAXIMO_DE_MENCOES = 50;

/**
 * `GET /v1/notificacoes/mencoes` — "Menções para mim" (#569): as dos últimos
 * 30 dias, vistas ou não, com as não lidas primeiro.
 */
export const MencoesParaMimSchema = z
  .object({ itens: z.array(NotificacaoSchema.extend({ vista: z.boolean() })) })
  .strict();

export type MencoesParaMim = z.infer<typeof MencoesParaMimSchema>;

/**
 * Marcar como vistas. Idempotente por natureza: marcar de novo o que já foi
 * visto não muda nada, e id de outra pessoa é ignorado em silêncio — dizer
 * "não é sua" confirmaria que existe.
 */
export const MarcarNotificacoesVistasSchema = z
  .object({ ids: z.array(IdSchema).min(1).max(MAXIMO_DE_NOTIFICACOES) })
  .strict();

export type MarcarNotificacoesVistas = z.infer<typeof MarcarNotificacoesVistasSchema>;

/** Quantas passaram a vistas nesta chamada — zero numa repetição. */
export const NotificacoesMarcadasSchema = z.object({ marcadas: z.number().int().min(0) }).strict();

export type NotificacoesMarcadas = z.infer<typeof NotificacoesMarcadasSchema>;

/**
 * `GET /v1/conversations/{id}/mencionaveis` — quem pode ser chamado com "@"
 * numa nota interna desta conversa (#569).
 *
 * Quem não pode ver a conversa vem na lista, marcado e com o porquê: a tela
 * avisa antes de enviar, em vez de mandar um link que a pessoa não abre.
 */
export const MencionaveisSchema = z
  .object({
    itens: z.array(
      z
        .object({
          usuario_id: IdSchema,
          nome: z.string(),
          foto_url: z.string().nullable(),
          pode_ver: z.boolean(),
          motivo: z.string().nullable(),
        })
        .strict(),
    ),
  })
  .strict();

export type Mencionaveis = z.infer<typeof MencionaveisSchema>;
